import { Directive, Input, OnInit } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { CreditCard } from '@df/session/api/credit-cards/credit-card';
import { NSagepay } from '../sagepay.namespace';
import { SagepayPiComponentDef } from './sagepay-pi.component.def';

@Directive()
export abstract class SagepayPiSavedCardComponentDef<T extends NSagepay.IReusableCreditCardData = NSagepay.IReusableCreditCardData>
    extends SagepayPiComponentDef<NSagepay.IReusableSubmissionPaymentData, T>
    implements OnInit
{
    @Input()
    card: CreditCard;

    override ngOnInit() {
        super.ngOnInit();

        this.subscriptions.add(
            this.events.on(Checkout.EVENTS.SavePaymentMethodBefore).subscribe((savePaymentData: Checkout.IQuoteDataPayment) => {
                if (savePaymentData.method === this.card?.code) {
                    savePaymentData.method = this.card.data.method;
                    savePaymentData.additional_data = {
                        cardId: this.card.data.id
                    };
                }
            })
        );

        this.subscriptions.add(
            this.events.on(Checkout.EVENTS.SubmitError).subscribe((errorData: Checkout.ISubmitErrorEventData) => {
                if (this.isSelectedInRequest(errorData.request)) {
                    this.onError(errorData);
                }
            })
        );
    }

    /**
     * Returns if this component method was selected in checkout submission
     * @param {Checkout.ISubmissionData} request
     * @returns {boolean}
     */
    protected isSelectedInRequest(request: Checkout.ISubmissionData): boolean {
        return request.paymentMethod.method === this.methodCode;
    }

    /**
     * Checks if method is currently selected one
     * @return {boolean}
     */
    override isSelected(): boolean {
        return this.card.code === this.checkoutFormComponent.paymentMethodCode;
    }

    get isCcvRequired(): boolean {
        return !!this.card?.isCcvRequired;
    }

    /**
     * Returns checkout submission payment data
     * @param {NSagepay.IOwnFormTokeniseResult} tokeniseResult
     * @param {string} merchantKey
     * @returns {NSagepay.IOwnFormCheckoutSubmissionPaymentData}
     */
    protected getSubmissionPaymentData(
        tokeniseResult: NSagepay.ITokeniseResult,
        merchantKey: string
    ): NSagepay.IReusableSubmissionPaymentData {
        return {
            method: this.card.data.method,
            additional_data: {
                'card-identifier': tokeniseResult.cardIdentifier,
                'sesion-key': merchantKey,
                cc_type: tokeniseResult.cardType,
                reusable: true,
                ...this.sagepayPiService.getBrowserInfo()
            }
        };
    }

    protected getTokeniseResult(merchantKeyResponse: NSagepay.IMerchantKeyResponse, data: T): Promise<NSagepay.ITokeniseResult> {
        return this.sagepayPiService.tokeniseReusableCard(merchantKeyResponse.spMerchantKey, this.card, data.cvc);
    }

    /**
     * Error callback
     * @param {Checkout.ISubmitErrorEventData} errorData
     */
    protected onError(errorData?: Checkout.ISubmitErrorEventData) {
        this.checkoutFormComponent.status.superBusy = false;

        if (errorData && errorData.error) {
            this.checkoutFormComponent.status.error = errorData.error;
        }

        this.checkoutFormComponent.status.errorData = errorData;

        this.checkoutFormComponent.dataError = errorData?.data_error;
    }
}